import { Box, Typography, Button, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import dayjs from "dayjs";
import CustomAxios from "../utils/CustomAxios";
import CircularProgress from '@mui/material/CircularProgress';

export function CustomDeductionResults() {
    const navigate = useNavigate();
    const location = useLocation();
    const [deductionResults, setDeductionResults] = useState([]);
    const [skippedDeductions, setSkippedDeductions] = useState([]);
    const [deduction, setDeduction] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [deletingRecord, setDeletingRecord] = useState(null);
    const [deleteRecordDialogOpen, setDeleteRecordDialogOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [deleteError, setDeleteError] = useState('');
    const [deleteSuccess, setDeleteSuccess] = useState(false);
    const [filters, setFilters] = useState({
        memberid: { value: null, matchMode: FilterMatchMode.CONTAINS },
        membername: { value: null, matchMode: FilterMatchMode.CONTAINS },
        state: { value: null, matchMode: FilterMatchMode.CONTAINS },
        estate: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const [skippedFilters, setSkippedFilters] = useState({
        memberid: { value: null, matchMode: FilterMatchMode.CONTAINS },
        membername: { value: null, matchMode: FilterMatchMode.CONTAINS },
        error: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });

    useEffect(() => {
        // Get data from location state or localStorage
        if (location.state?.deductionResults) {
            setDeductionResults(location.state.deductionResults);
            setDeduction(location.state.deductionStatus=="success" ? true : false);
        } else {
            // Fallback to localStorage if state is not available
            const storedResults = localStorage.getItem('customDeductionResults');
            if (storedResults) {
                try {
                    setDeductionResults(JSON.parse(storedResults));
                } catch (e) {
                    console.error('Error parsing stored results:', e);
                }
            }
        }
        
        if (location.state?.skippedDeductions) {
            setSkippedDeductions(location.state.skippedDeductions);
            setDeduction(location.state.deductionStatus=="success" ? true : false);
        }
    }, [location.state]);

    const handleDeleteRecordClick = (record) => {
        setRecordToDelete(record);
        setDeleteError('');
        setDeleteSuccess(false);
        setDeleteRecordDialogOpen(true);
    };

    const handleDeleteRecordConfirm = async () => {
        if (!recordToDelete || !recordToDelete.transdetailid) {
            setDeleteError('Invalid record. Missing transdetailid.');
            return;
        }
        
        setDeletingRecord(recordToDelete.transdetailid);
        setDeleteError('');
        
        try {
            await CustomAxios.post('oasis/delete_custom_deduction_record/', {
                transdetailid: recordToDelete.transdetailid
            });
            
            // Remove the deleted record from deductionResults
            setDeductionResults(prevResults => 
                prevResults.filter(record => record.transdetailid !== recordToDelete.transdetailid)
            );
            
            // Keep dialog open, show success
            setDeleteSuccess(true);
        } catch (error) {
            setDeleteError(error.response?.data?.error || 'Failed to delete record. Please try again.');
        } finally {
            setDeletingRecord(null);
        }
    };

    const handleDeleteRecordCancel = () => {
        setDeleteRecordDialogOpen(false);
        setRecordToDelete(null);
        setDeleteError('');
        setDeleteSuccess(false);
    };

    const exportToExcel = () => {
        const dataToExport = activeTab === 0 ? deductionResults : skippedDeductions;
        
        if (!dataToExport || dataToExport.length === 0) {
            return;
        }

        let rearrangedData;
        if (activeTab === 0) {
            // Processed deductions
            rearrangedData = dataToExport.map((item, index) => ({
                S_No: index + 1,
                M_No: item.memberid,
                Member_Name: item.membername,
                New_NRIC: item.newicno || '',
                Old_NRIC: item.oldicno || '',
                State: item.state || '',
                Estate: item.estate || '',
                Share_Balance: parseFloat(item.beginingsharebalance || 0).toFixed(2),
                Subscription_Balance: parseFloat(item.beginingsubsbalance || 0).toFixed(2),
                Age: item.age || '',
                Deducted_From: item.deducted_from || '',
                Deduction_Amount: item.deduction_amount || ''
            }));
        } else {
            // Skipped deductions
            rearrangedData = dataToExport.map((item, index) => ({
                S_No: index + 1,
                M_No: item.memberid,
                Member_Name: item.membername || '',
                Reason: item.error || 'Insufficient balance',
                Share_Balance: parseFloat(item.share_balance || 0).toFixed(2),
                Subscription_Balance: parseFloat(item.subscription_balance || 0).toFixed(2),
                Deduction_Amount: parseFloat(item.deduction_amount || 0).toFixed(2)
            }));
        }

        const worksheet = XLSX.utils.json_to_sheet(rearrangedData);
        const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
        const excelBuffer = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'array'
        });

        const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const data = new Blob([excelBuffer], {
            type: EXCEL_TYPE
        });

        const fileName = activeTab === 0 
            ? `processed_deductions_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`
            : `skipped_deductions_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.xlsx`;
        saveAs(data, fileName);
    };

    return (
        <Box className="fulldiv" style={{ width: "100%", height: "calc(100vh - 40px)", marginTop: "40px" }}>
            <Dialog open={deduction} >
                <DialogTitle>
                    <Typography sx={{ fontFamily: "inter", fontSize: "20px", fontWeight: "600", lineHeight: "28px", color: "#242424" }}>
                        Deduction Processed Successfully
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <Typography sx={{ fontFamily: "inter", fontSize: "14px", fontWeight: "400", lineHeight: "24px", color: "#242424" }}>
                            Processed Deductions: <strong style={{ color: "#288d2c" }}>{deductionResults.length}</strong>
                        </Typography>
                        <Typography sx={{ fontFamily: "inter", fontSize: "14px", fontWeight: "400", lineHeight: "24px", color: "#242424" }}>
                            Skipped Deductions:<strong style={{ color: "#d32f2f" }}> {skippedDeductions.length}</strong>
                        </Typography>
                    <Button variant="contained" onClick={() => setDeduction(false)} sx={{ width: "120px",alignSelf: "center" }}>
                        Close
                    </Button>
                    </Box>
                </DialogContent>
            </Dialog>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", height: { sm: 'initial', padding: "0px 20px 0px 20px" }, width: "100%" }}>
                {/* Header */}
                <Box sx={{ width: "100%", top: "72px", left: "304px", padding: "8px 0px 8px 0px", display: "flex", justifyContent: "space-between", borderBottom: "1px solid #CBD5E1", alignItems: "center", height: "84px" }}>
                    <Box sx={{ height: "48px", padding: "8px 0px 8px 0px", gap: "12px", display: "flex", alignItems: "center" }}>
                        <Box sx={{ width: "32px", height: "32px" }}>
                            <PriceChangeOutlinedIcon sx={{ width: "32px", height: "32px", color: "#288d2c" }} />
                        </Box>
                        <Typography sx={{ fontFamily: "inter", fontSize: "20px", fontWeight: "600", lineHeight: "28px", color: "#242424" }}>
                            Processed Deductions
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: "12px" }}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={exportToExcel}
                            disabled={
                                (activeTab === 0 && (!deductionResults || deductionResults.length === 0)) ||
                                (activeTab === 1 && (!skippedDeductions || skippedDeductions.length === 0))
                            }
                            sx={{
                                textTransform: "none",
                                fontFamily: "inter",
                                fontSize: "14px"
                            }}
                        >
                            Download
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/custom-deduction')}
                            sx={{
                                textTransform: "none",
                                fontFamily: "inter",
                                fontSize: "14px"
                            }}
                        >
                            Back
                        </Button>
                    </Box>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={(e, newValue) => setActiveTab(newValue)}
                        sx={{
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontFamily: 'inter',
                                fontSize: '16px',
                                fontWeight: 500,
                                minHeight: '48px'
                            },
                            '& .Mui-selected': {
                                color: '#288d2c',
                                fontWeight: 600
                            }
                        }}
                    >
                        <Tab 
                            label={`Processed Deductions (${deductionResults.length})`} 
                        />
                        <Tab 
                            label={`Skipped Deductions (${skippedDeductions.length})`} 
                        />
                    </Tabs>
                </Box>

                {/* Processed Deductions Tab */}
                {activeTab === 0 && (
                    deductionResults.length > 0 ? (
                    <Box sx={{ 
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px"
                    }}>
                        <Box sx={{ 
                            width: "100%",
                            maxWidth: "100%",
                            overflowX: "auto",
                            overflowY: "hidden",
                            "& .p-datatable-wrapper": {
                                overflowX: "auto",
                                maxWidth: "100%"
                            },
                            "& .p-column-filter": {
                                width: "100%",
                                "& input": {
                                    width: "100% !important",
                                    maxWidth: "100% !important",
                                    fontSize: "11px",
                                    padding: "2px 4px",
                                    boxSizing: "border-box"
                                }
                            },
                            "& .p-datatable .p-datatable-thead > tr > th": {
                                padding: "4px",
                                "& .p-column-filter input": {
                                    width: "100%",
                                    maxWidth: "100%"
                                }
                            }
                        }}>
                            <DataTable
                                value={deductionResults}
                                scrollable
                                style={{ 
                                    backgroundColor: "#F1F5F9"
                                }}
                                rows={10}
                                paginator
                                removableSort
                                filters={filters}
                                filterDisplay="row"
                                emptyMessage="No deductions processed."
                            >
                                <Column 
                                    header="SN" 
                                    headerStyle={{ width: '50px', maxWidth: '50px' }} 
                                    bodyStyle={{ width: '50px', maxWidth: '50px' }}
                                    body={(data, options) => options.rowIndex + 1}
                                />
                                <Column 
                                    field="memberid" 
                                    header="M.No" 
                                    headerStyle={{ width: "80px", maxWidth: "80px" }}
                                    bodyStyle={{ width: "80px", maxWidth: "80px" }}
                                    filter
                                    sortable
                                    filterPlaceholder=""
                                />
                                <Column 
                                    field="membername" 
                                    header="Member Name" 
                                    headerStyle={{ width: "140px", maxWidth: "140px" }}
                                    bodyStyle={{ width: "140px", maxWidth: "140px" }}
                                    filter
                                    sortable
                                />
                                <Column 
                                    field="nricnew" 
                                    header="New Nric" 
                                    headerStyle={{ width: "110px", maxWidth: "110px" }}
                                    bodyStyle={{ width: "110px", maxWidth: "110px" }}
                                />
                                <Column 
                                    field="nricold" 
                                    header="Old Nric" 
                                    headerStyle={{ width: "110px", maxWidth: "110px" }}
                                    bodyStyle={{ width: "110px", maxWidth: "110px" }}
                                />
                                <Column 
                                    field="state" 
                                    header="State" 
                                    headerStyle={{ width: "90px", maxWidth: "90px" }}
                                    bodyStyle={{ width: "90px", maxWidth: "90px" }}
                                    filter
                                    sortable
                                />
                                <Column 
                                    field="estate" 
                                    header="Estate" 
                                    headerStyle={{ width: "110px", maxWidth: "110px" }}
                                    bodyStyle={{ width: "110px", maxWidth: "110px" }}
                                    filter
                                    sortable
                                />
                                <Column 
                                    field="beginingsharebalance" 
                                    header="Share Balance" 
                                    headerStyle={{ width: "80px", maxWidth: "80px", textAlign: "center" }}
                                    bodyStyle={{ width: "80px", maxWidth: "80px", textAlign: "right" }}
                                    body={(rowData) => {
                                        const balance = parseFloat(rowData.beginingsharebalance || 0);
                                        return balance.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                    }}
                                />
                                <Column 
                                    field="beginingsubsbalance" 
                                    header="Subscription Balance" 
                                    headerStyle={{ width: "80px", maxWidth: "80px", textAlign: "center" }}
                                    bodyStyle={{ width: "80px", maxWidth: "80px", textAlign: "right" }}
                                    body={(rowData) => {
                                        const balance = parseFloat(rowData.beginingsubsbalance || 0);
                                        return balance.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                    }}
                                />
                                <Column 
                                    field="age" 
                                    header="Age" 
                                    headerStyle={{ width: "60px", maxWidth: "60px", textAlign: "center" }}
                                    bodyStyle={{ width: "60px", maxWidth: "60px", textAlign: "center" }}
                                />
                                <Column 
                                    field="deducted_from" 
                                    header="Deducted From" 
                                    headerStyle={{ width: "110px", maxWidth: "110px" }}
                                    bodyStyle={{ width: "110px", maxWidth: "110px" }}
                                />
                                <Column 
                                    header="Action" 
                                    headerStyle={{ width: "100px", maxWidth: "100px", textAlign: "center" }}
                                    bodyStyle={{ width: "100px", maxWidth: "100px", textAlign: "center" }}
                                    body={(rowData) => (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={deletingRecord === rowData.transdetailid ? (
                                                <CircularProgress size={16} sx={{ color: "#d32f2f" }} />
                                            ) : (
                                                <DeleteIcon />
                                            )}
                                            onClick={() => handleDeleteRecordClick(rowData)}
                                            disabled={deletingRecord === rowData.transdetailid || deletingRecord !== null}
                                            sx={{
                                                textTransform: "none",
                                                fontFamily: "inter",
                                                fontSize: "12px",
                                                color: "#d32f2f",
                                                borderColor: "#d32f2f",
                                                "&:hover": {
                                                    borderColor: "#b71c1c",
                                                    backgroundColor: "#ffebee"
                                                }
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                />
                            </DataTable>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ 
                        display: "flex", 
                        justifyContent: "center", 
                        alignItems: "center", 
                        minHeight: "200px" 
                    }}>
                        <Typography sx={{ 
                            fontFamily: "inter", 
                            fontSize: "16px", 
                            color: "#64748B" 
                        }}>
                            No processed deductions found
                        </Typography>
                    </Box>
                ))}

                {/* Skipped Deductions Tab */}
                {activeTab === 1 && (
                    skippedDeductions.length > 0 ? (
                        <Box sx={{ 
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px"
                        }}>
                            <Box sx={{ 
                                width: "100%",
                                maxWidth: "100%",
                                overflowX: "auto",
                                overflowY: "hidden",
                                "& .p-datatable-wrapper": {
                                    overflowX: "auto",
                                    maxWidth: "100%"
                                },
                                "& .p-column-filter": {
                                    width: "100%",
                                    "& input": {
                                        width: "100% !important",
                                        maxWidth: "100% !important",
                                        fontSize: "11px",
                                        padding: "2px 4px",
                                        boxSizing: "border-box"
                                    }
                                },
                                "& .p-datatable .p-datatable-thead > tr > th": {
                                    padding: "4px",
                                    "& .p-column-filter input": {
                                        width: "100%",
                                        maxWidth: "100%"
                                    }
                                }
                            }}>
                                <DataTable
                                    value={skippedDeductions}
                                    scrollable
                                    style={{ 
                                        backgroundColor: "#F1F5F9"
                                    }}
                                    rows={10}
                                    paginator
                                    removableSort
                                    filters={skippedFilters}
                                    filterDisplay="row"
                                    emptyMessage="No skipped deductions found."
                                >
                                    <Column 
                                        header="SN" 
                                        headerStyle={{ width: '50px', maxWidth: '50px' }} 
                                        bodyStyle={{ width: '50px', maxWidth: '50px' }}
                                        body={(data, options) => options.rowIndex + 1}
                                    />
                                    <Column 
                                        field="memberid" 
                                        header="M.No" 
                                        headerStyle={{ width: "80px", maxWidth: "80px" }}
                                        bodyStyle={{ width: "80px", maxWidth: "80px" }}
                                        filter
                                        sortable
                                        filterPlaceholder=""
                                    />
                                    <Column 
                                        field="membername" 
                                        header="Member Name" 
                                        headerStyle={{ width: "180px", maxWidth: "180px" }}
                                        bodyStyle={{ width: "180px", maxWidth: "180px" }}
                                        filter
                                        sortable
                                    />
                                    <Column 
                                        field="error" 
                                        header="Reason" 
                                        headerStyle={{ width: "250px", maxWidth: "250px" }}
                                        bodyStyle={{ width: "250px", maxWidth: "250px" }}
                                        filter
                                        sortable
                                    />
                                    <Column 
                                        field="share_balance" 
                                        header="Share Balance" 
                                        headerStyle={{ width: "120px", maxWidth: "120px", textAlign: "center" }}
                                        bodyStyle={{ width: "120px", maxWidth: "120px", textAlign: "right" }}
                                        body={(rowData) => {
                                            const balance = parseFloat(rowData.share_balance || 0);
                                            return balance.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                        }}
                                    />
                                    <Column 
                                        field="subscription_balance" 
                                        header="Subscription Balance" 
                                        headerStyle={{ width: "150px", maxWidth: "150px", textAlign: "center" }}
                                        bodyStyle={{ width: "150px", maxWidth: "150px", textAlign: "right" }}
                                        body={(rowData) => {
                                            const balance = parseFloat(rowData.subscription_balance || 0);
                                            return balance.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                        }}
                                    />
                                    <Column 
                                        field="deduction_amount" 
                                        header="Deduction Amount" 
                                        headerStyle={{ width: "140px", maxWidth: "140px", textAlign: "center" }}
                                        bodyStyle={{ width: "140px", maxWidth: "140px", textAlign: "right" }}
                                        body={(rowData) => {
                                            const amount = parseFloat(rowData.deduction_amount || 0);
                                            return amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                        }}
                                    />
                                </DataTable>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ 
                            display: "flex", 
                            justifyContent: "center", 
                            alignItems: "center", 
                            minHeight: "200px" 
                        }}>
                            <Typography sx={{ 
                                fontFamily: "inter", 
                                fontSize: "16px", 
                                color: "#64748B" 
                            }}>
                                No skipped deductions found
                            </Typography>
                        </Box>
                    )
                )}

                {/* Delete Record Confirmation Dialog */}
                <Dialog
                    open={deleteRecordDialogOpen}
                    onClose={(event, reason) => {
                        if (reason === 'backdropClick') return;
                        handleDeleteRecordCancel();
                    }}
                    disableEscapeKeyDown
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ fontFamily: "inter", fontSize: "18px", fontWeight: "600" }}>
                        Delete Deduction Record
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ fontFamily: "inter", fontSize: "14px", mb: 2 }}>
                            Are you sure you want to delete this deduction record? This action cannot be undone and will reverse the member's balance update.
                        </DialogContentText>
                        {recordToDelete && (
                            <Box sx={{ mt: 2, p: 2, backgroundColor: "#F1F5F9", borderRadius: "4px" }}>
                                <Typography sx={{ fontFamily: "inter", fontSize: "14px", fontWeight: "600", mb: 1 }}>
                                    Record Details:
                                </Typography>
                                <Typography sx={{ fontFamily: "inter", fontSize: "13px", mb: 0.5 }}>
                                    <strong>Member ID:</strong> {recordToDelete.memberid}
                                </Typography>
                                <Typography sx={{ fontFamily: "inter", fontSize: "13px", mb: 0.5 }}>
                                    <strong>Member Name:</strong> {recordToDelete.membername}
                                </Typography>
                                <Typography sx={{ fontFamily: "inter", fontSize: "13px", mb: 0.5 }}>
                                    <strong>Deduction Amount:</strong> {parseFloat(recordToDelete.deduction_amount || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                </Typography>
                                <Typography sx={{ fontFamily: "inter", fontSize: "13px" }}>
                                    <strong>Deducted From:</strong> {recordToDelete.deducted_from || 'N/A'}
                                </Typography>
                            </Box>
                        )}
                        {deleteError && (
                            <Typography sx={{ fontFamily: "inter", fontSize: "14px", color: "#d32f2f", mt: 2 }}>
                                {deleteError}
                            </Typography>
                        )}
                        {deleteSuccess && (
                            <Typography sx={{ fontFamily: "inter", fontSize: "14px", color: "#1f6d22", mt: 2 }}>
                                Record deleted successfully.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button
                            onClick={handleDeleteRecordCancel}
                            disabled={deletingRecord !== null}
                            sx={{
                                textTransform: "none",
                                fontFamily: "inter",
                                fontSize: "14px"
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeleteRecordConfirm}
                            disabled={deletingRecord !== null || deleteSuccess}
                            variant="contained"
                            color="error"
                            sx={{
                                textTransform: "none",
                                fontFamily: "inter",
                                fontSize: "14px"
                            }}
                        >
                            {deleteSuccess ? "Deleted" : deletingRecord ? "Deleting..." : "Delete"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
}

